"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import DecorativeFormBackground from "@/components/DecorativeFormBackground";

// A true 50/50 split, edge to edge -- a full-height real photo of Devina on the left, form on
// the right. The form stays first in DOM/tab order regardless (screen readers and keyboard
// navigation shouldn't have to pass through the decorative photo first); `lg:order-2` is what
// actually moves it to the right visually. The image column is hidden below `lg` rather than
// shrunk, since a half-width image on a phone-sized screen would squeeze the form into an
// unreadably narrow strip; the form alone (full width) is the right mobile layout, not a
// miniature split.
export default function FormSplitLayout({
  image,
  imageAlt,
  children,
}: {
  image: string;
  imageAlt: string;
  children: ReactNode;
}) {
  return (
    // grid-cols-1 (not just the implicit default) is load-bearing below `lg`: an unconstrained
    // auto column lets an oversized descendant (react-phone-number-input's number field, in
    // practice) blow the track past the viewport instead of shrinking to fit -- same class of
    // bug as the mermaid-diagram column overflow, fixed the same way. min-w-0 on the content
    // column backs it up for the same reason.
    // No near-full-viewport min-height below `lg` any more -- paired with the content column's
    // top alignment there, a short form (a bare phone-number field, an empty batch list) used to
    // be stretched into a lot of dead decorative-texture space below it. `lg:` keeps the floor
    // it always had, where the split-image column and centered content still want to fill the
    // screen.
    <div className="grid grid-cols-1 lg:min-h-[calc(100vh-72px)] lg:grid-cols-2">
      {/* Top-aligned below `lg` (a form that's short relative to a tall phone screen used to
          center itself into a lot of empty space above and below, which read as if the page
          hadn't finished loading) -- `justify-center` only kicks back in on desktop, where the
          image column beside it makes vertical centering read as intentional, not empty.
          min-h-[560px] is a middle ground, not the old full-viewport floor: enough room for a
          step's own content plus DatePicker's calendar popover (up to ~420px, see DatePicker.tsx)
          to open without needing to overlap the footer, short of stretching all the way to the
          bottom of the screen the way the removed calc(100dvh-Npx) floor did. */}
      {/* No overflow-hidden here (unlike the image column below) -- DatePicker's calendar
          popover lives inside `children` and an ancestor clip would cut it off whenever it's
          taller than the remaining space below the trigger. The decorative background doesn't
          need the clip either: it's `absolute inset-0`, which by definition can't extend past
          this `relative` parent regardless. */}
      <div className="relative flex min-h-[560px] min-w-0 flex-col justify-start px-6 py-6 sm:py-8 lg:order-2 lg:min-h-0 lg:justify-center lg:px-16 lg:py-4">
        <DecorativeFormBackground />
        <div className="relative">{children}</div>
      </div>
      <div className="relative hidden overflow-hidden lg:order-1 lg:block">
        {/* scale-110: several of these photos have a fair amount of empty sky/ground at the
            frame's edges once cropped to this column's aspect ratio -- a slight zoom keeps
            Devina the visually dominant subject instead of a small figure surrounded by margin.
            overflow-hidden on the parent clips the zoomed edges cleanly. */}
        <Image
          src={image}
          alt={imageAlt}
          fill
          sizes="50vw"
          className="scale-110 object-cover"
        />
        <div className="absolute inset-0 bg-white/20" />
      </div>
    </div>
  );
}
